import { isAdminRole } from '@/lib/roles';
import type { MePermission, PermissionScope, PermissionsMe, User } from '@/lib/types';

/*
 * What the UI is allowed to offer.
 *
 * ── 🔴 THE PERMISSION SET DECIDES, FOR EVERY ROLE — owner's decision 2026-10-01
 *
 * **The rule now:** every action on every module resolves from the signed-in
 * person's permission list (`/permissions/me`), and nothing else.
 *
 *   - `none`, or no row at all  → NOT allowed.
 *   - `own` or `all`            → allowed. The SERVER narrows `own` to the
 *     person's own records; the client just offers the control, and a 404 on a
 *     record they do not own is handled by the page that asked.
 *   - DELETE follows the set like every other action. There is no
 *     administrator-only rule any more, on any module.
 *   - `super_admin` alone bypasses the list (the resolver hands it the whole
 *     catalogue anyway). `admin` does NOT — `isAdmin` is kept on the ability
 *     for code that LABELS things, and `can()` never consults it.
 *   - While `/permissions/me` has not loaded, or has failed, every write fails
 *     CLOSED. Reads keep degrading open on the ordinary modules, and fail closed
 *     on the handful that only administrators were ever shown (see
 *     `PRIVILEGED_READ_MODULES`).
 *
 * The backend was changed in step: every `requireRole` guard was replaced by
 * the permission check. `api-keys`, `support-sessions` and `permission-sets`
 * became modules (read/create/update/delete) and, today, only the super-admin
 * set holds them.
 *
 * ⚠️ *SUPERSEDED 2026-10-01, everything below this heading that argues "the ROLE
 * decides, and permissions may only narrow it": `enforcedByRole`,
 * `ADMIN_WRITE_MODULES`, `ADMIN_READ_MODULES`, `ROLE_AUTHORITATIVE` and the admin
 * short-circuit in `can()` are DELETED.* The paragraphs are kept because they
 * record why the old model existed — the server was role-guarded and the sets
 * disagreed with it in both directions — and a reader meeting a stale role check
 * elsewhere in `src/` needs to know it is a leftover, not a rule.
 *
 * ── Why this is not just `permissions.some(p => p.key === …)` (SUPERSEDED) ──
 *
 * The backend ships a full permission model — 25 modules, 6 actions, 98
 * catalogue rows, five seeded sets — and enforces it on a few of them.
 *
 * 🔴 **"THREE OF THEM" WAS ALREADY WRONG WHEN IT WAS LAST READ, AND `approve`
 * HAS NOW MOVED THE ACTION COUNT TOO — 2026-09-15.** *SUPERSEDED: "and enforces
 * it on three of them."* `sales-tracker` made it four on 2026-09-11 and this
 * sentence did not move; `credit-notes` makes it five today. **No replacement
 * count is written here, deliberately** — `OPEN_WRITE_MODULES` below is the list
 * this file actually decides from, and a sentence that merely agrees with it
 * today is a second copy waiting to disagree. The action figure has moved too:
 * migration 200 adds `approve` to `f_actions`, so read {@link ActionSlug}, not
 * this line.
 *
 * ⚠️ *SUPERSEDED: "15 modules, 6 actions, 58 catalogue rows, four seeded sets
 * — and does not enforce any of it."* Those were a 2026-08-28 reading; the
 * figures above were taken 2026-09-04, after migration 145 added `integrations`
 * and `settings`. **That number has moved six times in a week and NOTHING in
 * `src/` reads it** — `PermissionGrid` derives its denominator from
 * `GET /permissions` — which is why six wrong readings cost this repo no code
 * at all, and why this line is a dated reading for orientation and must never
 * become an input to a control.
 *
 * A handful of modules gate on the permission sets, WITH a scope that really
 * narrows — **`OPEN_WRITE_MODULES` below is that list, and is the only place it
 * lives.** Every OTHER guard in the API is still `requireRole(...)`, and it is
 * those others the two paragraphs below are about. So `/permissions/me`
 * describes an intended model, while the server enforces a coarser one on the
 * role-guarded majority, and the two disagree in BOTH directions:
 *
 * ⚠️ *SUPERSEDED: "`tasks`, `notes` and `targets` gate on the permission sets …
 * Every other guard in the API is still `requireRole(...)`."* **A LIST is worse
 * than a count here, because it reads as exhaustive** — that one named three of
 * what are now five, so it also asserted that `sales-tracker` and
 * `credit-notes` are role-guarded, which is false and is the direction that
 * would have this file invent a refusal. Point at the set; do not re-list it.
 *
 *   OVER-reports — an `advanced_user` is told they hold 47 permissions. The
 *     server refuses 19 of them: every delete, and every write to products,
 *     cycles, intakes and the reference masters. Five named people would see
 *     an enabled Delete button that 403s on click.
 *
 *   UNDER-reports — nobody is granted `users.read`, but `GET /users` has no
 *     role guard at all, so every authenticated user can read the staff list.
 *     Hiding it would hide a working page.
 *
 *   FICTIONAL — empty today. `*.export` permissions and `api-logs` write
 *     permissions describing endpoints that do not exist were removed from the
 *     catalogue. The `export` guard below survives as a backstop, not because
 *     anything grants it today.
 *
 * So the rule WAS: **the ROLE decides, and permissions may only narrow it.**
 * ⚠️ *SUPERSEDED 2026-10-01 — see the top of this comment.*
 *
 * "When the backend switches enforcement on, `ENFORCED` below is the thing to
 * delete" — it switched it on, and `enforcedByRole` is deleted.
 */

/* --- the Dashboards and Trackers nav surfaces ---------------------------- */

/*
 * The sidebar surfaces in the Dashboards and Trackers sections, as permission
 * modules. Backend migration `108_add_dashboard_tracker_permissions.sql` is the
 * source of truth for the slugs and the grants.
 *
 * ⚠️ **THIS OBJECT AND THE SERVER'S CATALOGUE ARE NOT THE SAME LIST, AND
 * NEITHER IS A COUNT TO KEEP IN STEP.** `dashboards-overview` was seeded by 108
 * and is deliberately NOT listed here (see the note on the object below);
 * `dashboards-operations` IS listed here and is seeded by a migration of its
 * own. **Do not "correct" either side by counting the other**: the object is
 * what this app gates, the catalogue is what an administrator may tick, and they
 * are allowed to differ in both directions.
 *
 * ⚠️ *SUPERSEDED: "SEVEN here, and 108 created EIGHT."* A count of members in an
 * object anybody may add to — falsified by the next surface, with nothing
 * failing, which is precisely the artefact this block goes on to warn about.
 *
 * The owner asked for these sections to appear in permission sets: four actions
 * each, and the effect is to hide the nav row and the page. **The effect is
 * CLIENT-SIDE ONLY** — no endpoint stands behind any of these modules and no
 * server guard consults them, which is consistent with the rest of the model,
 * where enforcement is off for most modules.
 *
 * 🔴 **THE SLUGS ARE FROZEN LITERALS AND ARE NO LONGER DERIVABLE FROM THE NAV —
 * corrected 2026-09-02.** *SUPERSEDED: "The slug is derivable rather than a
 * table anybody maintains: **nav group label lowercased, plus the row label
 * kebab-cased.** A further surface therefore needs no decision, only the same
 * rule applied."*
 *
 * That rule described how the members below were NAMED, and the owner renamed
 * every one of the rows on 2026-09-02 — `Management` → `Management Dashboard`,
 * `Invoice` → `Invoice Tracker`, `My Tasks` → `Tasks Tracker`, and four more.
 * Applied to the nav as it stands today the rule yields
 * `dashboards-management-dashboard` and `trackers-tasks-tracker`, which are not
 * the slugs **migration 108 seeded server-side** and not the slugs an
 * administrator ticks in `/permission-sets`.
 *
 * ⚠️ **Nothing broke, and that is precisely why this had to be corrected by
 * hand.** These are string literals in the object below, not a computation, so
 * the rename could not move them; `tsc` saw nothing, no render changed, and the
 * only artefact that went wrong was this sentence. **A derivation rule stated in
 * prose beside a hardcoded table is falsified by an edit in another file, with
 * every instrument reporting success.**
 *
 * **So a further surface needs a DECISION, not a rule**: pick a slug, and get it
 * seeded server-side. A slug is a join key against a permission catalogue in
 * somebody else's database — it may look like the row label and it does not
 * follow it, and **renaming a row must never rename one of these.**
 *
 * ⚠️ These are the only members of `ModuleSlug` that name NO API endpoint.
 * There is no `requireRole` behind any of them and no request they gate; the
 * modules exist solely so an administrator can hide a client-side surface. That
 * is why `enforcedByRole` answers them separately, and why `can()` treats an
 * explicit `none` differently from an absent key for these and these alone.
 *
 * ⚠️ **Gate on `read` ONLY.** The migration grants `create`/`update`/`delete` to
 * the administrator set, but all three are inert on every one of them — there is
 * nothing on a dashboard to create, update or delete. `enforcedByRole` refuses
 * them outright so a control can never be gated on one; a button behind
 * `canUpdate('dashboards-management')` would be permanently disabled with no
 * true explanation to give.
 *
 * ⚠️ Nothing else in `src/` spells one of these strings out. Every call site
 * reads a key off this object, so a rename reaches all of them and `tsc` catches
 * a key that no longer exists.
 */
export const NAV_SURFACE_MODULE = {
  /*
   * ⚠️ **`dashboardOverview` (`dashboards-overview`) IS GONE.** The Overview
   * dashboard was deleted — it rendered invented figures — so the row and the
   * page it gated no longer exist, and a key here with no consumer is
   * indistinguishable from a surface somebody forgot to gate.
   *
   * ⚠️ **The SERVER may still be listing that slug.** Migration 108 created
   * eight modules and a backend migration removing this one is landing
   * separately, so for a window `GET /permissions` returns a module this file
   * does not name. **That is harmless in both directions and neither side
   * needs to wait for the other**: the permission grid renders FROM the
   * response, so an extra module simply appears as one more row an
   * administrator can tick, and nothing here reads the slug. Do not re-add the
   * key to "match" the API — the union is the list of surfaces this app gates,
   * not a mirror of the catalogue.
   */
  /*
   * ⚠️ **THE ROW NAMES BELOW WERE ALL RENAMED ON 2026-09-02 AND THE SLUGS WERE
   * NOT.** Each comment names the row as it reads TODAY; the slug beside it is
   * migration 108's and is fixed. Where the two no longer resemble each other,
   * the slug is right — see the correction above.
   */
  /**
   * Sidebar “University Partnerships Dashboard” → `/university-partnerships`.
   *
   * ⚠️ **Also the DEFAULT POST-SIGN-IN LANDING PAGE**, so denying it is louder
   * than denying any other row here: it is the first screen of the day. It
   * degrades open — only an explicit `none` scope denies — and
   * `use-landing-page.ts` sends a denied reader to the first page they CAN see
   * rather than into the page's own refusal panel.
   */
  dashboardUniversityPartnerships: 'dashboards-university-partnerships',
  /**
   * The account lead switcher on that dashboard (migration 329, owner
   * 2026-10-27). Super Admin only by default; without it the dashboard shows
   * the viewer alone.
   */
  upLeadSwitcher: 'dashboards-up-lead-switcher',
  /** Sidebar “Management Dashboard” → `/management`, since 2026-09-02. */
  dashboardManagement: 'dashboards-management',
  /**
   * Sidebar “Accounts Dashboard” → `/accounts-dashboard`.
   *
   * ⚠️ *SUPERSEDED: "routeless, renders the inert “Soon” state."* It gained its
   * page and this line was not re-read — the fourth time a comment here has
   * described whether a page EXISTS, which is a fact that goes stale the day
   * somebody builds one. **`nav.tsx` is the only place that answer lives**: a row
   * with no `to` is the “Soon” state.
   */
  dashboardAccounts: 'dashboards-accounts',
  /**
   * Sidebar “Operations Dashboard” → `/operations`, and the landing page for
   * the Operations access level.
   *
   * ⚠️ **The slug was DECIDED, not derived** — see the correction above. It is
   * seeded by a backend migration of its own, separately from this change, and
   * it is a join key: once it exists it does not move.
   *
   * ✅ **SHIPPING THIS CLIENT BEFORE THE SLUG IS SEEDED IS SAFE, AND THE
   * DIRECTION IS THE WHOLE REASON.** With no row for this module in
   * `/permissions/me`, `scopeOf` returns `undefined`, and `can()` resolves an
   * absent scope on a NAV-SURFACE module as ALLOWED for `read` — so the row is
   * simply visible to everyone until the migration lands. **Nothing is
   * withdrawn from anybody.**
   *
   * ⛔ **This is exactly what `integrations` is NOT, which is why that one sits
   * outside this object.** `integrations.update` is a real capability, so
   * shipping ITS client half first resolved an absent scope as DENIED and took
   * connect/disconnect away from two access levels for two days — a truthful
   * -looking refusal for a deploy that had not happened. A nav-surface module
   * can only ever over-offer a VIEW, on a page with no server guard behind it,
   * which the reader could reach by URL regardless. That asymmetry is the
   * licence to ship in this order, and it does not generalise.
   */
  dashboardOperations: 'dashboards-operations',
  /**
   * Sidebar “Tasks Tracker” → `/tasks`.
   *
   * ⚠️ **NOT the `tasks` module.** `tasks` is permission-set enforced and
   * governs the records; this is a view control over one nav row and its page.
   * Never gate the tracker on `tasks`, and never gate task records on this —
   * they are different questions, and the API enforces only the first.
   */
  trackerMyTasks: 'trackers-my-tasks',
  /** Sidebar “Client Deliverables Tracker” — /client-deliverables. */
  trackerClientDeliverables: 'trackers-client-deliverables',
  /**
   * Sidebar “Future Billables Tracker” — /future-billables-tracker.
   *
   * ⚠️ *SUPERSEDED 2026-09-04: “Sidebar ‘Invoice Tracker’ — /invoice-tracker,
   * since 2026-09-02.”* The owner retired the Invoice Tracker; that page is
   * deleted and its route is gone, and this module now gates the Future
   * Billables Tracker that replaced it.
   *
   * ⛔ **THE SLUG `trackers-invoice` DOES NOT MOVE, AND THE MEMBER KEEPS ITS
   * NAME.** A module slug is a join key: the seeded permission sets and every
   * guard resolve this module by that exact string, so renaming it would
   * silently strip the grant from everyone who holds it, with nothing failing —
   * the same trap `reference-data` carries, whose DISPLAY NAME became “Entity
   * Catalog” while its slug stayed put. A backend migration renames this
   * module's label to “Future Billables Tracker” for the permission grid; the
   * slug on the wire is untouched, and so is `trackerInvoice` here.
   *
   * ⚠️ These four comments described whether a page EXISTS, which is a fact that
   * goes stale the day somebody builds one — and two of them had, silently. Read
   * `nav.tsx`: a row with no `to` is the “Soon” state, and that file is the only
   * place the answer lives.
   */
  trackerInvoice: 'trackers-invoice',
  /**
   * Sidebar “Revenue Tracker” — /revenue-tracker.
   *
   * ⚠️ *SUPERSEDED 2026-09-04: “routeless, renders ‘Soon’.”* A parallel session
   * built the page and this line was not re-read — the fifth time a comment in
   * this block has described whether a page EXISTS, which is the fact here that
   * goes stale fastest and the one nothing can fail on. **`nav.tsx` is the only
   * place that answer lives**: a row with no `to` is the “Soon” state.
   */
  trackerRevenue: 'trackers-revenue',
} as const;

export type NavSurfaceModule = (typeof NAV_SURFACE_MODULE)[keyof typeof NAV_SURFACE_MODULE];

/*
 * ⚠️ *SUPERSEDED 2026-10-01, and deleted: `NAV_SURFACE_MODULES`, the membership
 * test `enforcedByRole` and `can()` used to treat these modules specially
 * (writes always refused, an absent read allowed).* They resolve from the set
 * like every other module now.
 */

/**
 * The permission-module slugs.
 *
 * `reference-data` covers every picklist. The `NavSurfaceModule` members are
 * the sidebar surfaces above and have no endpoint behind them; `integrations`
 * and `settings` are the same shape arriving on two pages that DO have
 * endpoints — see the notes at those members.
 *
 * ⛔ **`reference-data` is the SLUG and it does not move.** Its display NAME
 * became **Entity Catalog** server-side, and the slug is the join key that
 * resolves the seeded sets — renaming it here would disconnect every set from
 * every grant it holds. Nothing in this app should ever spell the display name
 * as a slug. The name arrives on the `GET /permissions` response and the grid
 * renders it from there, so no client change was needed for the rename and none
 * should be made.
 */
export type ModuleSlug =
  // Migration 350 (2026-10-28): the Management dashboard "Sales targets" tab
  // (read + update); Management and Super Admin `all`, every other set `none`.
  | 'sales-targets'
  // Migration 329 (2026-10-27): the UP dashboard account lead switcher.
  | 'dashboards-up-lead-switcher'
  // Migration 312 (2026-10-23): the follow-ups tracker is a view control (the
  // route reads under `companies.read`); the email log carries read + triage.
  | 'trackers-prospect-follow-ups'
  | 'email-log'
  | 'deals'
  | 'quotes'
  | 'invoices'
  | 'deliverables'
  | 'companies'
  | 'contacts'
  | 'products'
  | 'cycles'
  | 'intakes'
  | 'users'
  | 'reference-data'
  | 'api-logs'
  | 'tasks'
  | 'notes'
  | 'projects'
  /*
   * ⚠️ **`targets` IS enforced server-side and was missing from this union
   * entirely** — added 2026-09-02. `/targets` carries no `requireRole`; like
   * `/tasks` and `/notes` it gates on the permission sets, WITH a scope that
   * really narrows. So a super-admin has been ticking four boxes no screen in
   * this app could read, which is the quietest possible failure: the grid saved
   * cleanly, the server honoured the grant, and nothing here consulted it.
   *
   * ⚠️ **Adding the slug does NOT gate anything on its own**, and nothing calls
   * it yet. `UserTargetsModal` deliberately renders its controls and handles the
   * refusal where it arrives — `?subjectId=` naming somebody else without the
   * audit grant is a 403, not a narrowed list, and the modal makes that a
   * first-class state. Do not "finish" this by gating that modal on a slug: the
   * 403 is a per-request answer about one subject, and this union cannot ask it.
   */
  | 'targets'
  /*
   * ⛔ **THE ONLY MODULE IN THIS SYSTEM THAT DISCLOSES PAY** — migration 205,
   * and the sixth gated on permission sets rather than roles, after `tasks`,
   * `notes`, `targets`, `sales-tracker` and `credit-notes`.
   *
   * Owner decision 24 is ADMINISTRATORS ONLY: no sales lead sees a commission
   * figure in EMERGE, not even their own. Migration 205 grants it to
   * `management` and `super-admin` and to nobody else, with a guard counting
   * OFFENDERS so a permission set created next year is caught the first time
   * somebody grants it commissions by mistake.
   *
   * ⛔ **NOT a `NAV_SURFACE_MODULE` member, and it must never become one.** Two
   * independent reasons, and the second is the serious one:
   *
   *   - That set forces `create`/`update`/`delete` to a hard `false`, which
   *     would delete every affordance the module exists to gate — a scheme is a
   *     record somebody builds, assigns and runs.
   *   - A nav surface hides a page this app draws while the API behind it
   *     answers the same to everybody — **hiding a page is not refusing a
   *     request**. Filing this among them would tell the next reader that the
   *     figures are protected by a view control. They are protected by the
   *     grants, and a view control protects nothing.
   *
   * ⚠️ **THE NAV ROWS CARRY `adminOnly` AS WELL AS THIS SLUG, and they are the
   * first rows in `nav.tsx` to carry both.** Module gating DEGRADES OPEN — an
   * absent module is allowed — so an unseeded catalogue, an older API or a
   * failed `/permissions/me` would otherwise put a route to everybody's pay in
   * a sales lead's sidebar. `adminOnly` is what fails closed; the slug is what
   * still hides the row from an administrator whose grant was revoked.
   */
  | 'commissions'
  /*
   * The FOURTH permission-set-enforced module, after `tasks`, `notes` and
   * `targets` — a REAL server guard, not a view control.
   *
   * ⛔ **NOT a `NAV_SURFACE_MODULE` member, and it must never become one.** That
   * set forces `create`/`update`/`delete` to a hard `false` because a dashboard
   * has nothing to change; a pursuit is a record somebody creates, moves between
   * columns and deletes, so folding it in there would delete the very
   * affordances the module exists to gate. It is in `OPEN_WRITE_MODULES` below
   * instead, with its three siblings, which is what keeps DELETE out of the
   * admin-only arm — this module's delete is whatever the set grants, and the
   * standing "delete is admin-only across the API" rule now has four exceptions
   * rather than three.
   *
   * Grants come from backend **migration 164**: full CRUD to
   * `university-partnerships` and `management` (and super-admin by the
   * resolver's own bypass), READ to `accounts` and `operations`. So the nav row
   * is not `adminOnly` — the row's tier follows the endpoint's READ guard, and
   * read is granted to every set.
   *
   * 🔴 **ORDER: SERVER, THEN CLIENT.** An absent scope on a module that is not a
   * nav surface resolves as allowed for `read` and **DENIED for `update`**, so
   * shipping this client ahead of the migration takes create, update and delete
   * away from the two sets that are supposed to hold them — and the symptom is a
   * disabled control with a truthful-looking refusal beside it, which reads as a
   * permissions decision somebody made rather than as a deploy that has not
   * happened. Admins short-circuit before the list is read, *which is precisely
   * why nobody testing as an admin would see it*. `integrations` cost two tiers
   * two days of Xero connect/disconnect exactly this way.
   */
  | 'sales-tracker'
  /*
   * The FIFTH permission-set-enforced module, after `tasks`, `notes`, `targets`
   * and `sales-tracker` — a REAL server guard, not a view control. Backend
   * migration `200_add_credit_notes_permissions.sql` catalogues it at
   * `/api/v1/credit-notes` with FIVE permissions, and `credit-notes.routes.ts`
   * carries **no `requireRole` anywhere**: every handler reaches
   * `requirePermission`, which consults `userCan`, and the grant decides.
   *
   * ⛔ **NOT a `NAV_SURFACE_MODULE` member, and it must never become one** — the
   * migration says so in as many words. That set forces
   * `create`/`update`/`delete` to a hard `false` because a dashboard has nothing
   * to change; a credit note is a document somebody raises, approves, voids and
   * deletes, so folding it in there would delete the very affordances the module
   * exists to gate. Same standing as `sales-tracker` directly above.
   *
   * ⛔ **NOT an `ADMIN_WRITE_MODULES` member either.** Writes are granted to
   * `accounts`, not to administrators alone, so answering `admin` would be this
   * app inventing a refusal against the tier the module was built for. It is in
   * `OPEN_WRITE_MODULES` instead, which is what keeps DELETE out of the
   * admin-only arm: a credit note's delete is whatever `credit-notes.delete`
   * grants, and `accounts` deliberately holds none of it — the module offers
   * VOIDING instead, which leaves the row and its audit trail standing.
   *
   * Grants, from migration 200: full CRUD **and `approve`** to `management` and
   * `super-admin`; `read`/`create`/`update` to `accounts`; `read` alone to
   * `university-partnerships` and `operations`. **Read reaches every seeded
   * set**, so the nav row is not `adminOnly` — a row's tier follows the
   * endpoint's READ guard, and a company's credit BALANCE is derived from these
   * records and shown on the UP dashboard and the company record.
   *
   * 🔴 **`approve` IS A FIFTH ACTION AND `accounts` DELIBERATELY DOES NOT HOLD
   * IT** — see {@link ActionSlug} and {@link Ability.canApprove}. The person who
   * raises a credit note is not the person who makes it spendable, and migration
   * 200's Guard 5 pins that as a refusal rather than as a comment.
   *
   * 🔴 **ORDER: SERVER, THEN CLIENT** — the same hazard recorded at
   * `sales-tracker` above. An absent scope on a module that is not a nav surface
   * resolves as allowed for `read` and **DENIED for every write**, so shipping
   * this client ahead of migration 200 takes create and update away from
   * `accounts` — and the symptom is a disabled control with a truthful-looking
   * refusal beside it, which reads as a permissions decision somebody made
   * rather than as a deploy that has not happened. Admins short-circuit before
   * the list is read, *which is precisely why nobody testing as an admin would
   * see it*.
   */
  | 'credit-notes'
  /*
   * What institutions told the public 2027 festival interest form — the
   * `/event-interest` tracker and the read-only panel on a company record.
   * Another module gated on the permission sets rather than on roles, the
   * `tasks` / `notes` / `targets` / `sales-tracker` / `credit-notes` class.
   *
   * **Measured in `event-interest.routes.ts` on 2026-09-17, not assumed:**
   * `requireRole` appears in that router only inside COMMENTS — three hits, one
   * of them a paragraph explaining at length why there is no real one — and
   * `router.use(authenticate)` is the only router-wide guard. The three staff
   * reads reach `requirePermission`, which consults `userCan`. So it is in
   * `OPEN_WRITE_MODULES` below, and the note there carries the rest.
   *
   * ⛔ **NOT a `NAV_SURFACE_MODULE` member, and the NAV is what makes that trap
   * live rather than theoretical.** This module's row sits under **Trackers**,
   * where most of its neighbours ARE nav surfaces — so the obvious move when
   * adding a tracker is a `trackerEventInterest` key beside theirs. It would be
   * wrong twice over: that set forces `create`/`update`/`delete` to a hard
   * `false` because a dashboard has nothing to change, and it files a module
   * among the view controls, where **hiding a page is explicitly not refusing a
   * request**. There are real records and a real server guard behind this one.
   * `sales-tracker` above is the row in that same group that got it right first.
   *
   * ⛔ **NOT an `ADMIN_WRITE_MODULES` member either.** The router states the
   * grants go to the staff sets, "because staff are who the module is for", so
   * answering `admin` would be this app inventing a refusal against the tier the
   * module was built for.
   *
   * 🔴 **`event-interest.create` DOES NOT GATE SUBMITTING THE FORM, AND NOTHING
   * IN THIS APP SUBMITS ONE.** `POST /event-interest/submissions` is reached by
   * `admin-app`'s SERVER carrying an API KEY, and it is authorised by the
   * `event-interest.submit` SCOPE stored against that key — never by a
   * permission and never by a role, because "what may this user do in EMERGE" is
   * not the question being asked of a credential that is not a person. The same
   * router refuses a key on all three staff reads for the mirror-image reason.
   * **So this slug answers for the READS**, and a "New submission" control gated
   * on `canCreate` would be a button for a capability this client does not have.
   *
   * 🔴 **ORDER: SERVER, THEN CLIENT — AND THE PERMISSIONS MIGRATION IS NOT
   * WRITTEN YET.** Measured 2026-09-17: nothing in `migrations/` catalogues this
   * module, so it is absent from `/permissions/me` for everybody. An absent scope
   * on a module that is not a nav surface resolves ALLOWED for `read` and DENIED
   * for every write — which costs nothing today only because **both surfaces are
   * read-only**, and stops being free the moment one grows a write. That is the
   * hazard `integrations` paid two tiers two days of Xero connect/disconnect for,
   * and it is weaker here rather than absent. ⚠️ **The event that closes it is
   * the migration applying and `GET /permissions` returning the rows** — not a
   * date, not a deploy, and not somebody remembering.
   */
  | 'event-interest'
  /*
   * What a visitor asked for through the B2B site's **"Get In Touch"** form —
   * the Proposals section of the `/event-interest` tracker. Another module
   * gated on the permission sets rather than on roles: the
   * `tasks` / `notes` / `targets` / `sales-tracker` / `credit-notes` /
   * `event-interest` class, so a bare slug and **not** a `NAV_SURFACE_MODULE`
   * key.
   *
   * **Measured in `b2b-proposal.routes.ts` on 2026-09-20, not assumed:** the
   * module publishes a machine `POST /submissions` under
   * `requireScope('b2b-proposal.submit')` and two staff reads under
   * `denyApiKey`, and **no `requireRole` anywhere** — the staff reads call
   * `requirePermission`, which consults `userCan`. So it is in
   * `OPEN_WRITE_MODULES` below.
   *
   * 🔴 **IT EXISTS SO THE PROPOSALS PILL CAN GATE ITSELF, AND THAT IS THE WHOLE
   * REASON THE SLUG IS HERE.** The owner chose *"a tab on `/event-interest`, as
   * asked"* with the cost stated — **one page now holds TWO modules' data.**
   * That page gates on `event-interest`, and migration 219 grants
   * `event-interest.read` to **`university-partnerships` AND `operations`**
   * while migration 230 grants `b2b-proposal.read` to
   * **`university-partnerships` alone**. ⛔ **So without this slug the
   * Proposals pill rides on the WRONG module's grant** and shows an
   * `operations` reader enquiries they hold no grant for.
   *
   * ⚠️ **THE SEPARATING POPULATION IS EMPTY TODAY, WHICH IS WHY NOTHING HAS
   * REPORTED IT.** `operations` maps to the `viewer` role and **`viewer` has
   * held nobody since 2026-09-15** — so the divergent branch is real in the
   * grant table and unreachable in the data. **One account put back on
   * `viewer` makes it live**; re-measure the role distribution rather than
   * reading the absence of complaints as correctness.
   *
   * ⛔ **ADDING IT HERE WITHOUT ADDING IT TO `OPEN_WRITE_MODULES` INVERTS THE
   * BUG.** Declared here and absent there, `enforcedByRole` answers for the
   * module and resolves every write as **administrator-only**, against a server
   * that grants it on the permission set — so this app would invent a refusal.
   * **`tsc` cannot see it: one is a union, the other a `Set` of strings.** Both
   * move together or neither does.
   *
   * ⚠️ **IT DOES NOT GATE THE ROW ACTIONS AND MUST NOT BE MADE TO.** The owner
   * ruled the three tiers deliberately non-uniform — **archive on the read
   * grant, delete ADMIN, convert any write role** — so `ProposalsPanel` gates
   * delete on `ability.isAdmin` and convert on `ability.canCreate('deals')`,
   * which is the module the convert actually writes to. Swapping either for
   * `canDelete('b2b-proposal')` would hand the delete to whatever the grant
   * says and quietly overturn that ruling.
   *
   * 🔴 **`b2b-proposal.submit` IS A KEY SCOPE, NOT A PERMISSION, AND NOTHING IN
   * THIS APP SUBMITS A PROPOSAL.** The ingest is reached by the B2B site's
   * SERVER carrying an API key; the scope is ticked on `/api-keys`. ⛔ Never
   * gate a control on it and never add it to {@link ActionSlug}.
   */
  | 'b2b-proposal'
  /**
   * 🔴 **THE REPORT-DOWNLOAD LEAD LOG — gated on permission sets, the same class
   * as {@link 'b2b-proposal'} above**, and for the same reasons: its staff reads
   * carry no `requireRole` (the grant IS the refusal), and the machine ingest is
   * authorised by the API-key scope `report-download.submit`, which is not a
   * permission and must never be added to {@link ActionSlug}.
   *
   * ⛔ **IT MOVES WITH ITS `OPEN_WRITE_MODULES` ENTRY OR NOT AT ALL.** Declared
   * here and absent there, `enforcedByRole` resolves every write as
   * administrator-only and this app invents a refusal. `tsc` sees neither half.
   *
   * ⚠️ **It gates the SECTION, not the row actions** — the tiers are the
   * proposals ruling copied: archive on the read grant, delete ADMIN, convert
   * `canCreate('deals')`. Swapping either for `canDelete('report-download')`
   * would hand the delete to whatever the grant says.
   */
  | 'report-download'
  /*
   * ⚠️ **CLIENT-ENFORCED ONLY, both of these** — the same standing as the nav
   * surfaces above, and they must never be described, labelled or built as
   * access control. **Hiding a control is not refusing a request.** The API
   * behind both pages answers whoever calls it exactly as it did before these
   * modules existed, so no refusal sentence here may imply the data is
   * protected.
   *
   * ⚠️ **They are NOT members of `NAV_SURFACE_MODULE`, and must not be added to
   * it.** That set forces `create`/`update`/`delete` to `false` because there is
   * nothing on a dashboard to change. `integrations.update` is a real control
   * that connects and disconnects Xero, so folding it in there would delete the
   * only affordance the module exists to gate.
   */
  | 'integrations'
  | 'settings'
  /*
   * Split out of `settings` and `reference-data` — owner's decision 2026-10-02.
   * Each copies the grants of the module it came from, so behaviour is unchanged
   * until somebody ticks them differently. `settings` keeps the advanced screen;
   * `reference-data` keeps every master not named in one of the four below.
   */
  | 'settings-notifications'
  | 'settings-documents'
  | 'settings-email'
  | 'settings-maps'
  | 'reference-geography'
  | 'reference-sales'
  | 'reference-planning'
  | 'reference-people'
  /*
   * New modules, owner's decision 2026-10-01: these three screens were
   * `super_admin` by role and are now gated by the set. Today only the
   * super-admin set holds them, so behaviour is unchanged until somebody
   * ticks them for another set.
   */
  | 'api-keys'
  | 'support-sessions'
  | 'permission-sets'
  /*
   * The Marketing section — backend migration 256. Real server guards
   * (`requireGrant`), granted to `management` and `super-admin` at scope `all`
   * and explicitly `none` to every other set. `marketing-campaigns` also carries
   * `send` and `approve`; `marketing-lists` carries `export`. See
   * `docs/api/marketing.md` in the backend repo.
   */
  | 'marketing-campaigns'
  | 'marketing-lists'
  | 'marketing-templates'
  | 'marketing-email-accounts'
  | 'marketing-settings'
  /*
   * Product Insights → SEED FAB — backend migration 289 (owner 2026-10-18).
   * Real server guard: `read` to `management` and `university-partnerships`,
   * `update` (edit school ↔ company links) to `management` only, `none`
   * elsewhere.
   */
  | 'product-insights'
  /**
   * Migration 319 (owner 2026-10-25): editing a company's account lead history.
   * `update` only, `all` to `management` (super admin bypasses), `none` elsewhere.
   * Reading the history rides `companies.read`.
   */
  | 'account-lead-history'
  | NavSurfaceModule;

/**
 * ⚠️ **`approve` EXISTS ON `credit-notes` ALONE, exactly as `convert` exists on
 * `quotes` alone** — backend migration `200_add_credit_notes_permissions.sql`
 * adds it to `f_actions` citing `convert` as its precedent, and grants it to the
 * two administrator sets only.
 *
 * ⛔ **IT IS REFUSED ON EVERY OTHER MODULE BY `enforcedByRole`, STRUCTURALLY,
 * AND THAT GUARD IS NOT DECORATION.** Without it `can('approve', 'invoices')`
 * answers **true for an administrator**: the admin arm of `can()`
 * short-circuits before the permission list is read, so an absent
 * `invoices.approve` never gets the chance to deny it. The result would be a
 * capability the API has never had, lighting up a control for exactly the people
 * most likely to press it, with nothing failing.
 *
 * ✅ **`convert` IS CLOSED THE SAME WAY — 2026-09-18.** *SUPERSEDED: "`convert`
 * has the identical hole and is covered only by `canConvertQuote()` hardcoding
 * its module — which protects the sugar and not `can()` itself."* Both are now
 * refused structurally by `enforcedByRole`; see the two adjacent guards at the
 * top of that function, which are one rule written twice.
 *
 * ⚠️ **The two are narrowed at the signature by DIFFERENT SHAPES, and only one
 * of them has a parameter to get wrong.** {@link Ability.canApprove} narrows its
 * module argument to `'credit-notes'`; {@link Ability.canConvertQuote} takes no
 * module at all. ⛔ **Neither narrowing reaches `can()`**, which is flat — so a
 * module-specific action added here needs the `enforcedByRole` guard, not just a
 * tidy signature.
 *
 * ⚠️ *SUPERSEDED 2026-10-02: "`export` is kept as a backstop against a stale set
 * resurrecting a dead capability."* `export` is REAL again — owner's decision:
 * every export endpoint and every PDF download is gated on it. `send`, `triage`
 * and `manage-access` arrived in the same change. Where each exists is
 * {@link actionExists}; the narrowed helpers are on {@link Ability}.
 */
export type ActionSlug =
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  | 'convert'
  | 'approve'
  | 'export'
  | 'send'
  | 'triage'
  | 'manage-access';

/** Modules that carry `send` (DocuSign, quote mark-sent, Xero, email via Xero). */
export type SendModule =
  | 'quotes'
  | 'invoices'
  | 'credit-notes'
  | 'companies'
  | 'marketing-campaigns';
/** Modules that carry `export` (export endpoints and PDF downloads). */
export type ExportModule =
  | 'companies'
  | 'contacts'
  | 'deals'
  | 'quotes'
  | 'invoices'
  | 'credit-notes'
  | 'trackers-invoice'
  | 'trackers-revenue'
  | 'dashboards-accounts'
  | 'marketing-lists';
/** Modules that carry `triage` (archive / unarchive, link a company or deal). */
export type TriageModule = 'event-interest' | 'b2b-proposal' | 'report-download' | 'email-log';
/** Modules that carry `approve`. */
export type ApproveModule = 'credit-notes' | 'commissions' | 'marketing-campaigns';

const SEND_MODULES: ReadonlySet<string> = new Set<SendModule>([
  'quotes',
  'invoices',
  'credit-notes',
  'companies',
  'marketing-campaigns',
]);
const EXPORT_MODULES: ReadonlySet<string> = new Set<ExportModule>([
  'companies',
  'contacts',
  'deals',
  'quotes',
  'invoices',
  'credit-notes',
  'trackers-invoice',
  'trackers-revenue',
  'dashboards-accounts',
  'marketing-lists',
]);
const TRIAGE_MODULES: ReadonlySet<string> = new Set<TriageModule>([
  'event-interest',
  'b2b-proposal',
  'report-download',
  'email-log',
]);
const APPROVE_MODULES: ReadonlySet<string> = new Set<ApproveModule>([
  'credit-notes',
  'commissions',
  'marketing-campaigns',
]);

/*
 * ⚠️ *SUPERSEDED 2026-10-01, and DELETED: `ADMIN_WRITE_MODULES` (products,
 * cycles, intakes, reference-data, users, api-logs, settings — writes admin-only
 * however the set was ticked), `ADMIN_READ_MODULES` (api-logs, settings — admin
 * even to read), and `WRITE_ROLES`.* The backend gates all of them on the
 * permission set now, so the set is the whole answer here too.
 */

/**
 * Modules whose READ fails CLOSED while `/permissions/me` has not loaded or has
 * failed — the screens only administrators were shown under the old model.
 *
 * Every other read keeps degrading open in that window (the existing
 * behaviour), because blanking the whole app for the second before the list
 * lands, or for ever when the fetch fails, is worse than a brief over-offer of
 * a view the server will refuse anyway. These five are different: offering the
 * audit log or the permission sets to everybody on a failed fetch puts a row in
 * every sidebar that only ever answers 403.
 *
 * ⚠️ A super-admin bypasses this like everything else.
 */
const PRIVILEGED_READ_MODULES: ReadonlySet<string> = new Set([
  'api-logs',
  'settings',
  // The settings split, 2026-10-02 — same footing as `settings`.
  'settings-notifications',
  'settings-documents',
  'settings-email',
  'settings-maps',
  'api-keys',
  'support-sessions',
  'permission-sets',
  /*
   * Marketing (migration 256) — granted to the two administrator sets only and
   * explicitly `none` elsewhere, so a failed `/permissions/me` must not put five
   * rows that only ever answer 403 into every sidebar.
   */
  'marketing-campaigns',
  'marketing-lists',
  'marketing-templates',
  'marketing-email-accounts',
  'marketing-settings',
  // Product Insights (migration 289) — `none` for Operations and Accounts.
  'product-insights',
  // Super Admin only by default (migration 329); a failed load must not open it.
  'dashboards-up-lead-switcher',
  // Sales targets (migration 350): Management + Super Admin only; plan and
  // revenue figures must not open on a failed load.
  'sales-targets',
]);

/**
 * Actions that do not exist on a module at all, whatever a set says. The ONLY
 * structural refusals left in this file — they are not role rules, they are
 * facts about the catalogue, and a stale or over-generous set must not light
 * them up.
 *
 *   - `export`, `send`, `triage`: the module lists above (2026-10-02).
 *     *SUPERSEDED: "`export`: no endpoint exists".*
 *   - `manage-access`: `users` alone.
 *   - `approve`: `credit-notes`, `commissions` (the server checks
 *     `commissions.approve` on a run) and `marketing-campaigns` (a campaign
 *     held for approval). *SUPERSEDED: credit-notes alone.*
 *   - `convert`: catalogued on `quotes` alone (migration 028).
 */
function actionExists(action: ActionSlug, moduleSlug: string): boolean {
  if (action === 'export') return EXPORT_MODULES.has(moduleSlug);
  if (action === 'send') return SEND_MODULES.has(moduleSlug);
  if (action === 'triage') return TRIAGE_MODULES.has(moduleSlug);
  if (action === 'manage-access') return moduleSlug === 'users';
  if (action === 'approve') return APPROVE_MODULES.has(moduleSlug);
  if (action === 'convert' && moduleSlug !== 'quotes') return false;
  return true;
}

/*
 * ⚠️ *SUPERSEDED 2026-10-01, and DELETED: `OPEN_WRITE_MODULES` (the modules
 * whose routers carried no role guard, so the set decided — tasks, notes,
 * targets, sales-tracker, credit-notes, commissions, event-interest,
 * b2b-proposal, report-download), `ROLE_AUTHORITATIVE` (empty since migration
 * 032) and `enforcedByRole`.* Every module now behaves the way those nine
 * always did: the grant decides, delete included.
 */

/*
 * ⚠️ The `enforcedByRole` comments on `approve` and `convert` (2026-09-18) are
 * summarised by {@link actionExists}: both actions exist on one module each, and
 * the guard that refuses them elsewhere no longer has an admin short-circuit
 * to protect against — `can()` reads the list for every role but super_admin.
 */
/*
 * ⚠️ The nav surfaces (Dashboards and Trackers): `enforcedByRole` forced their
 * create/update/delete to a hard `false` as "inert". *SUPERSEDED 2026-10-01* —
 * every action resolves from the set, and `trackers-invoice.update` is now a
 * REAL capability: the backend gates Future Billables writes on it.
 */
/*
 * ⚠️ The 2026-10-01 viewer arm (a viewer's writes deferring to the set while
 * every other role's were decided by role) is subsumed: EVERY role's writes
 * defer to the set now, and every write fails closed until the set has loaded.
 */

/** The signed-in user's advisory permission list, indexed for lookup. */
export interface PermissionState {
  /** False until `/permissions/me` answers. Gating stays conservative until then. */
  loaded: boolean;
  /** True if the fetch failed — the UI falls back to role-only gating and says so. */
  failed: boolean;
  isSuperAdmin: boolean;
  byKey: ReadonlyMap<string, PermissionScope>;
  /**
   * Where this person's PERMISSION SETS say they should land after signing in,
   * already resolved by the server. `null` means no set they hold names one.
   *
   * 🔴 **THE SERVER RESOLVES IT AND THIS CLIENT COULD NOT — that is why it
   * arrives pre-decided rather than as a list to choose from.** A person may
   * hold several sets, and `/permissions/me` deliberately strips the SETS out
   * and returns only the resolved permissions, so a client never learns which
   * sets produced them and has nothing to rank. The owner's earlier ruling
   * (2026-08-31) rejected ranking sets in the client for exactly this reason;
   * the tie-break moved to the server rather than to us.
   *
   * ⚠️ **A DEFAULT, NOT A DECISION — the person's OWN stored preference wins
   * over it.** `resolveLandingPathWithSet` in `use-landing-page.ts` owns that
   * ladder and states the reasoning; do not re-derive the order at a call site.
   *
   * ⚠️ **NOT a claim the path is reachable.** An administrator can name a page
   * this reader may not open, and an unreachable landing page is a trap — the
   * screen that would let them change it sits behind the refusal they land on.
   * Every rung is re-tested for reachability where it is resolved.
   *
   * ⚠️ **`null` also covers an API that does not send the field yet** — this app
   * deploys separately from the server and can be newer than it. Both mean "no
   * set-level landing page applies", which is the same behaviour, so they are
   * deliberately not distinguished here.
   */
  landingPath: string | null;
}

export const EMPTY_PERMISSIONS: PermissionState = {
  loaded: false,
  failed: false,
  isSuperAdmin: false,
  byKey: new Map(),
  /*
   * Null before `/permissions/me` answers AND after it fails. In both cases
   * nothing is known about a set-level landing page, and the resolver falls
   * through to the reader's own preference and then the default — which is
   * exactly what happened before this field existed.
   */
  landingPath: null,
};

export function toPermissionState(me: PermissionsMe): PermissionState {
  const byKey = new Map<string, PermissionScope>();
  for (const entry of me.permissions as MePermission[]) byKey.set(entry.key, entry.scope);
  return {
    loaded: true,
    failed: false,
    isSuperAdmin: me.isSuperAdmin,
    byKey,
    /*
     * ⚠️ `?? null` rather than a bare read: the field is OPTIONAL on
     * `PermissionsMe` because an older server omits it entirely, and
     * `undefined` leaking into the ability would make `landingPath` mean three
     * things instead of two. Normalised once, here, at the border.
     */
    landingPath: me.landingPath ?? null,
  };
}

export interface Ability {
  /**
   * True for `admin` and `super_admin`.
   *
   * ⛔ **A LABEL, NOT A GATE — 2026-10-01.** The set decides every action for
   * every role but super_admin, and `can()` never consults this. Use it to
   * describe a person (a badge, a sentence), never to show, hide or enable a
   * control; ask `can()` for that.
   */
  readonly isAdmin: boolean;
  /**
   * True for `super_admin` alone.
   *
   * The API lets any admin write permission sets, but this app deliberately
   * restricts the whole permissions area to super_admin — being STRICTER than
   * the server is always safe, and access control is the one screen where a
   * narrower audience is the right default.
   */
  readonly isSuperAdmin: boolean;
  /**
   * True for the `viewer` role.
   *
   * ⚠️ *SUPERSEDED 2026-10-01: "the only role that may not write anything."*
   * **A viewer's writes are now decided by its permission set** (Operations may
   * create and update companies and deliverables), so this is a ROLE fact and
   * must NOT gate a write control or word a refusal as "read-only access" —
   * ask `can()` instead. `denialReason` no longer uses it.
   */
  readonly readOnly: boolean;
  /** Whether `/permissions/me` has answered. */
  readonly permissionsLoaded: boolean;
  /**
   * The landing page this person's permission SETS name, resolved by the
   * server, or `null` when none of them does.
   *
   * ⚠️ It is a DEFAULT and loses to the reader's own stored preference — see
   * {@link PermissionState.landingPath}, which carries the whole reasoning, and
   * `resolveLandingPathWithSet` in `use-landing-page.ts`, which owns the ladder.
   * ⛔ Do not re-derive the precedence at a call site.
   */
  readonly landingPath: string | null;
  can(action: ActionSlug, moduleSlug: ModuleSlug): boolean;
  /** Sugar for the four common ones. */
  canRead(moduleSlug: ModuleSlug): boolean;
  canCreate(moduleSlug: ModuleSlug): boolean;
  canUpdate(moduleSlug: ModuleSlug): boolean;
  canDelete(moduleSlug: ModuleSlug): boolean;
  /**
   * May this person ADD or EDIT a future billable (a planned document on the
   * Future Billables tracker)?
   *
   * 🔴 2026-10-01: `can('update', 'trackers-invoice')` — the backend now gates
   * future billables on the `trackers-invoice` module, set-decided like every
   * other action. *SUPERSEDED: "Operations (`viewer`) may add and edit future
   * billables, on top of everybody who may already update invoices" —
   * `FUTURE_BILLABLE_WRITE_ROLES` on the server, a role rule.*
   */
  readonly canWriteFutureBillables: boolean;
  /**
   * Quotes only. A write role, not admin.
   *
   * ⛔ **IT TAKES NO MODULE, AND THAT IS THE SIGNATURE HALF OF THE GUARD, not a
   * convenience.** `convert` exists on `quotes` alone — `028_create_permission_
   * catalogue.sql` seeds it `WHERE m.slug = 'quotes'` — so there is no second
   * module to offer and a call site cannot name a wrong one. It is strictly
   * stronger than {@link Ability.canApprove}, which narrows a parameter that
   * still exists. ⛔ **Do not give this one a module argument** to "match" it.
   *
   * ⚠️ **The signature is only half.** `can('convert', 'invoices')` typechecks —
   * `can` is flat — so `actionExists` refuses `convert` on every module but
   * `quotes`, whatever a set says. 2026-10-01: this is `can('convert',
   * 'quotes')` and nothing else — the set decides, for every role.
   *
   * ⚠️ **NOT the question for deal→quote or for partial billing.** Raising a
   * quote from a deal is CREATING a quote (`canCreate('quotes')`) and there is no
   * `deals.convert` row anybody could ever hold; `POST /quotes/{id}/invoices`
   * produces an INVOICE and asks `canCreate('invoices')`. `DealDetailPage` and
   * `QuoteDetailPage` each state this at their call site. Asking this instead
   * would gate one endpoint on another's permission row.
   *
   * ⚠️ **The gate is the optimisation; the 403 is the guarantee.** A role can
   * change under a live access token, which lasts days here.
   */
  canConvertQuote(): boolean;
  /**
   * Credit notes only — the fifth action, catalogued by backend migration 200 on
   * the `convert` precedent.
   *
   * ⛔ **THE PARAMETER IS NARROWED TO THE ONE MODULE THAT HAS THE ACTION, the
   * way {@link hasAudit} is**, so `canApprove('invoices')` does not typecheck
   * rather than answering a plausible `false` about a capability that does not
   * exist. `enforcedByRole` refuses it structurally as well: **the type stops a
   * call site and the guard stops `can()` being reached directly, and the admin
   * short-circuit needs both.** A second module gaining `approve` widens this
   * union and that line together — one decision, made twice on purpose.
   *
   * 🔴 **THIS IS A GRANT, NOT A TIER, AND IT IS A SEPARATION OF DUTIES.**
   * `accounts` holds `read`/`create`/`update` and deliberately NOT `approve`, so
   * the person who raises a credit note is not the person who makes its money
   * spendable. ⛔ **Never gate an Approve control on `isAdmin`.** The two agree
   * today only because migration 200 happens to have granted `approve` to the
   * two administrator sets; a super-admin may move it to another set without
   * demoting anybody, and the server would honour that while a role check would
   * not. That is exactly the coincidence {@link hasAudit} already warns about.
   *
   * ⚠️ **The gate is the optimisation; the 403 is the guarantee.** This module
   * really does enforce the grant server-side — `POST /credit-notes/:id/approve`
   * answers `403 PERMISSION_REQUIRED` from `requirePermission` regardless of
   * what was rendered — and a role or a grant can change under a live access
   * token, which lasts days here. Handle the refusal at the call site; a hidden
   * button is not a guarantee.
   *
   * ✅ *SUPERSEDED 2026-10-01: "`isAdmin` still short-circuits inside `can()`,
   * so this can OVER-offer to an administrator whose set had
   * `credit-notes.approve` removed."* It no longer does: `can()` bypasses for
   * `super_admin` alone, matching the server.
   */
  canApprove(moduleSlug: ApproveModule): boolean;
  /**
   * 2026-10-02: sending a record to somebody outside EMERGE — DocuSign send,
   * remind, void, refresh and signing links; quote mark-sent; every Xero push,
   * sync, create, link, unlink and resolve; emailing an invoice through Xero.
   * Narrowed to the modules that carry `send`.
   */
  canSend(moduleSlug: SendModule): boolean;
  /**
   * 2026-10-02: every export endpoint and every PDF download of the entity.
   * Narrowed to the modules that carry `export`.
   */
  canExport(moduleSlug: ExportModule): boolean;
  /**
   * 2026-10-02: archive / unarchive an enquiry and link a company or deal to it.
   * Narrowed to the three enquiry modules.
   */
  canTriage(moduleSlug: TriageModule): boolean;
  /**
   * 2026-10-02: `users.manage-access` — reset a password, change a role, send
   * an onboarding email. Ordinary user edits stay `canUpdate('users')`.
   */
  canManageAccess(): boolean;
  /**
   * Whether this user may see EVERYONE's tasks or notes, rather than only their
   * own — `scope: 'all'` on the module's `read`.
   *
   * **This is the first place a permission scope decides behaviour**, and it is
   * a grant rather than a tier: it can be given to somebody who is not an
   * administrator and taken away without demoting them. So gate the "everyone's
   * tasks" view on THIS, never on `isAdmin`. Today only the administrator set
   * holds it, which is exactly the sort of coincidence that tempts a role check.
   *
   * ⚠️ **`targets` has the same grant and is deliberately NOT in this type**,
   * checked 2026-09-02 rather than assumed either way. `/targets` and
   * `/targets/actuals` both guard on `subjectId !== actorId && !canSeeAll`, so
   * `scope: 'all'` on `targets.read` is an audit grant in exactly this sense and
   * the member would be correct.
   *
   * It is left out because it would have **no consumer and one wrong use**.
   * `UserTargetsModal` renders its controls and treats the 403 as a first-class
   * state, which is the house rule for a refusal that is per-request rather than
   * per-account: this grant answers "may I read ANYONE's", while the modal has
   * to ask "may I read THIS person's" — and your own id is exempt at every
   * scope, so a reader with no grant at all still sees their own targets. A
   * `hasAudit('targets')` gate in front of that modal would hide from every
   * ungranted user the one subject they are always allowed. Add the member when
   * a screen genuinely needs "everyone's targets", not before.
   */
  hasAudit(moduleSlug: 'tasks' | 'notes'): boolean;
  /** The declared scope, for surfacing an explicit `none` differently to an absence. */
  scopeOf(action: ActionSlug, moduleSlug: ModuleSlug): PermissionScope | undefined;
}

/**
 * Build the ability object for a user.
 *
 * Both arguments may be absent — a signed-out user, or a permission fetch that
 * has not landed. Everything fails closed in that state except reads, which
 * every authenticated role holds and which the server does not gate.
 */
export function createAbility(
  user: User | null | undefined,
  permissions: PermissionState = EMPTY_PERMISSIONS,
): Ability {
  const role = user?.role ?? '';
  const isAdmin = isAdminRole(role);
  const authenticated = user != null;

  function scopeOf(action: ActionSlug, moduleSlug: ModuleSlug): PermissionScope | undefined {
    return permissions.byKey.get(`${moduleSlug}.${action}`);
  }

  function can(action: ActionSlug, moduleSlug: ModuleSlug): boolean {
    if (!authenticated) return false;

    // A capability that does not exist on this module, whatever a set says.
    if (!actionExists(action, moduleSlug)) return false;

    /*
     * The ONE read outside the set — owner's decision 2026-10-01: the staff
     * list stays readable by everyone, and `GET /users` stays open to any
     * signed-in person server-side, because every staff picker in the app reads
     * it. Users-page WRITES follow `users` create/update/delete like anything
     * else.
     */
    if (action === 'read' && moduleSlug === 'users') return true;

    /*
     * A super_admin is handed the entire catalogue by the resolver, so their
     * permission list can never narrow anything. Short-circuiting also keeps
     * the lock-out escape hatch working if a set is misconfigured. The ROLE is
     * trusted before the list loads (and if it fails); once it has loaded the
     * resolver's own flag — read from the stored role rather than the JWT —
     * must agree, so a demotion takes effect without waiting for the token.
     *
     * ⛔ `admin` is NOT here. Owner's decision 2026-10-01: the set decides for
     * every role but super_admin.
     */
    if (permissions.isSuperAdmin) return true;
    if (role === 'super_admin' && (!permissions.loaded || permissions.failed)) return true;

    /*
     * Until `/permissions/me` has answered, or when it failed: every WRITE
     * fails closed — there is nothing to decide it from, and offering a write
     * the set may not hold is the over-offer this model exists to stop. READS
     * keep their previous behaviour and degrade open, except on the privileged
     * screens, so the app does not blank for a second on every load.
     */
    if (!permissions.loaded || permissions.failed) {
      if (action !== 'read') return false;
      return !PRIVILEGED_READ_MODULES.has(moduleSlug);
    }

    /*
     * ⚠️ *SUPERSEDED 2026-10-01: the `ROLE_AUTHORITATIVE` arm, where the role
     * answered an action the catalogue was known to be wrong about.* It had been
     * empty since migration 032, and with the server now gating on the set there
     * is no role answer left for it to give.
     */

    /*
     * 🔴 **THE SET IS THE WHOLE ANSWER — owner's decision 2026-10-01.** `own` and
     * `all` allow (the server narrows `own` to the person's records); `none`
     * and a missing row deny, on every module and every action.
     */
    const scope = scopeOf(action, moduleSlug);
    if (scope === 'own' || scope === 'all') return true;
    return false;
  }

  /*
   * ⚠️ *SUPERSEDED 2026-10-01, and deleted: the arms that let an ABSENT row
   * allow a read — on every module (the old "reads follow the server", which
   * kept `/users` readable to sets lacking `users.read`) and on the nav surfaces
   * ("degrade open", so an unseeded dashboard module stayed visible).* Owner's
   * decision: a missing row means NOT allowed. The server is gated on the set
   * now, so an absent read is a 403 there too, and hiding the control is no
   * longer hiding a working page.
   *
   * The 2026-09-13 note survives as fact: the permission grid writes an
   * explicit `scope: 'none'` on an untick (`DENY_SCOPE` in
   * `@/lib/permission-grid`), and `/permissions/me` is fetched once per
   * session, so a change lands at the affected person's next sign-in.
   */
  return {
    isAdmin,
    /*
     * Read from the role, and cross-checked against the resolver's own
     * `isSuperAdmin` once it has loaded — that flag is resolved from the
     * stored role rather than the JWT, so a demotion takes effect without
     * waiting for the token to expire (which is 5.28 days here).
     */
    isSuperAdmin: role === 'super_admin' && (!permissions.loaded || permissions.isSuperAdmin),
    // A ROLE fact only (the `viewer` role). Never a write gate — see the field.
    readOnly: authenticated && role === 'viewer',
    permissionsLoaded: permissions.loaded,
    /*
     * Passed through untouched. It is the SERVER's answer and this client has
     * nothing to add to it: the resolution across several sets happened there,
     * and the precedence against the reader's own preference happens in
     * `resolveLandingPathWithSet`, not here.
     */
    landingPath: permissions.landingPath,
    can,
    canRead: (moduleSlug) => can('read', moduleSlug),
    canCreate: (moduleSlug) => can('create', moduleSlug),
    canUpdate: (moduleSlug) => can('update', moduleSlug),
    canDelete: (moduleSlug) => can('delete', moduleSlug),
    /*
     * 2026-10-01: the backend gates future-billable writes on the
     * `trackers-invoice` module. *SUPERSEDED: `can('update', 'invoices') ||
     * role === 'viewer'`.*
     */
    canWriteFutureBillables: can('update', 'trackers-invoice'),
    canConvertQuote: () => can('convert', 'quotes'),
    /*
     * Routed through `can` like every other action rather than reading the scope
     * directly: the super-admin bypass, the not-yet-loaded fallback and the
     * failed-fetch fallback are all decisions this must not take a second copy
     * of. The module narrowing lives in the signature and in `actionExists`.
     */
    canApprove: (moduleSlug) => can('approve', moduleSlug),
    canSend: (moduleSlug) => can('send', moduleSlug),
    canExport: (moduleSlug) => can('export', moduleSlug),
    canTriage: (moduleSlug) => can('triage', moduleSlug),
    canManageAccess: () => can('manage-access', 'users'),
    hasAudit: (moduleSlug) => {
      // A super_admin holds the whole catalogue at `all` by the resolver's own
      // bypass, so they always have it. Everyone else must actually be granted
      // it — and until /permissions/me answers, assume they were not: showing a
      // colleague's queue by accident is worse than showing it a moment late.
      if (permissions.isSuperAdmin) return true;
      return scopeOf('read', moduleSlug) === 'all';
    },
    scopeOf,
  };
}

/** The one refusal sentence (owner's decisions 2026-09-24 and 2026-10-01). */
export const PERMISSION_SET_REFUSAL =
  'Your permission set does not include this. An administrator can change that.';

/**
 * Why a control is disabled, for a tooltip or an inline note.
 *
 * Says the true reason rather than "administrators only", which was wrong for
 * a viewer and is now wrong for every role but super_admin.
 */
export function denialReason(
  ability: Ability,
  action: ActionSlug,
  moduleSlug: ModuleSlug,
): string | null {
  if (ability.can(action, moduleSlug)) return null;
  // 2026-10-02: `export` is a real grant now. *SUPERSEDED: "Exporting is not
  // available yet."* A module that does not carry an action says so instead.
  if (!actionExists(action, moduleSlug)) return 'This is not available here.';
  /*
   * 🔴 **ONE NEUTRAL SENTENCE FOR EVERY REFUSAL — owner's decision 2026-10-01.**
   * The set is the whole answer for every role, so what anybody refused here
   * lacks is a grant, and the fix is an administrator ticking it. *SUPERSEDED:
   * "Deleting is restricted to administrators.", "Editing this is restricted to
   * administrators.", "There is nothing on this screen to change." (for a nav
   * surface's write — `trackers-invoice.update` is a real capability now) and
   * "Your account does not have permission for this."*
   *
   * The `actionExists` branch above keeps a nonexistent action from reading as
   * a grant somebody could tick.
   */
  return PERMISSION_SET_REFUSAL;
}

/*
 * ⚠️ The record of the previous wording rules, kept as history:
 */
/*
   * A READ refused by an explicit `none` in the permission set, on a module
   * that is not a nav surface — new on 2026-09-04, with the `can()` arm that
   * makes it reachable.
   *
   * ⚠️ **Before the `readOnly` line, for exactly the reason the nav-surface
   * branch is.** A `viewer` denied Contacts is not denied it for being
   * read-only: reading is the whole of what they were refused, and "your
   * account has read-only access" would send them to ask for a WRITE role that
   * would change nothing. `readOnly` is true for every viewer, so without this
   * branch it answers first and answers wrongly for all of them.
   *
   * ✅ **ONE NEUTRAL SENTENCE FOR EVERY EXPLICIT-`none` READ REFUSAL — owner's
   * decision 2026-09-24**, shared with the nav-surface branch above. ⚠️
   * *SUPERSEDED: "An administrator has hidden this screen from your account."*
   * That wording claimed a SCREEN was hidden, which was wrong in two directions:
   * on an enforced module the data really is protected (so it understated), and
   * on a view-only one nothing was "hidden" by a person at all. The new sentence
   * states the fact — the permission set lacks the grant — and where the fix is,
   * and asserts nothing about whether the API would refuse.
   *
   * 🔴 **WHO REACHES THIS BRANCH — corrected 2026-09-24.** ⚠️ *SUPERSEDED:
   * "Only a module carrying an explicit `none` reaches here, which means
   * `OPEN_WRITE_MODULES`."* **Any non-nav-surface module whose read scope is an
   * explicit `none` reaches it** — that includes the enforced
   * `OPEN_WRITE_MODULES` (`credit-notes`, `commissions`, `tasks`, `notes`,
   * `targets`, …, where the server also answers `403`) AND ordinary
   * role-guarded modules a permission set can still untick for viewing, such as
   * `contacts`, `invoices`, `products`, `quotes` and `integrations`, where the
   * API does not refuse the read and the refusal is this app's view decision.
   * The one wording is deliberately true of both.
   */

/**
 * Why this person may not add or edit a future billable, or `null` when they
 * may. The companion to {@link Ability.canWriteFutureBillables}, which asks
 * `trackers-invoice.update` (2026-10-01).
 */
export function futureBillablesDenialReason(ability: Ability): string | null {
  if (ability.canWriteFutureBillables) return null;
  return denialReason(ability, 'update', 'trackers-invoice');
}
